import { computed, ref } from 'vue';
import { envelopeReason, memberFacingMessage, PbApiError } from 'pb-api-errors';

/**
 * One payer's saved payment methods, as the multi-card panel reads and changes them through the
 * collection API (W-7..W-11). hive and checkout each bind it to their own request client
 * (`composables/usePaymentMethods.ts`) and feed `methods` into `PaymentMethodsPanel.vue`.
 */

export interface PaymentMethod {
    id: string;
    type: 'card' | 'link';
    brand?: string | null;
    last_four?: string | null;
    exp_month?: number | null;
    exp_year?: number | null;
    name?: string | null;
    link_email?: string | null;
    is_primary: boolean;
}

/** What a change to the collection sends: the verb, and the body when it has one. */
export interface PaymentMethodsChange {
    method: 'POST' | 'PUT' | 'PATCH' | 'DELETE';
    body?: Record<string, unknown>;
}

/**
 * The app's request client, as both apps' `usePbApi` answer: a read when no change is given, it
 * resolves rather than throws on a non-2xx, and `data` is the body the server answered with — the
 * v3 error envelope on a refusal.
 */
export type PaymentMethodsRequest = (
    url: string,
    change?: PaymentMethodsChange,
) => Promise<{ data: unknown; status: number; message: string }>;

/**
 * `scope` picks the payer the collection belongs to: `people` for a member's own cards, `companies`
 * for the cards a business is billed on. Both collections publish the same shape from the same
 * backend services, so everything below is payer-agnostic.
 */
export function paymentMethodsCollection(
    request: PaymentMethodsRequest,
    ownerId: () => number | undefined,
    scope: 'people' | 'companies' = 'people',
) {
    const methods = ref<PaymentMethod[]>([]);
    const loading = ref(false);
    const error = ref<string | null>(null);

    // Which read owns the list. A payer can change under the collection — checkout reuses its page
    // when `?company=` does, hive when the person changes — and a slow read for the previous one
    // must not land on top of the new payer's cards: the panel would show them, and an order would
    // be charged a card that belongs to someone else.
    let sequence = 0;

    const base = (): string | null => {
        const id = ownerId();
        return id ? `/${scope}/${id}/financial/payment-methods` : null;
    };

    async function fetch(): Promise<void> {
        const url = base();
        if (!url) return;

        const mine = ++sequence;
        const forOwner = ownerId();

        loading.value = true;
        error.value = null;
        try {
            const answer = await request(url);
            if (mine !== sequence || ownerId() !== forOwner) return;   // superseded

            if (answer.status === 200 && answer.data) {
                methods.value = (answer.data as { data?: PaymentMethod[] }).data ?? [];
            } else {
                error.value = memberFacingMessage(answer, 'Failed to load payment methods');
            }
        } finally {
            if (mine === sequence) loading.value = false;
        }
    }

    // The request client resolves (rather than throws) on a non-2xx, so a failure is invisible
    // unless the status is checked. A refusal leaves as a PbApiError carrying the envelope's
    // reason, which is what lets the UI show the API's own sentence for a refusal it made on
    // purpose ("This card is already on file.") and its own otherwise.
    async function mutate(url: string, change: PaymentMethodsChange): Promise<void> {
        const { data, status, message } = await request(url, change);
        if (status < 200 || status >= 300) {
            throw new PbApiError(message, status, envelopeReason(data));
        }
    }

    async function setPrimary(pmId: string): Promise<void> {
        const url = base();
        if (!url) return;
        await mutate(`${url}/${pmId}/primary`, { method: 'PUT' });
        await fetch();
    }

    async function edit(pmId: string, payload: Record<string, unknown>): Promise<void> {
        const url = base();
        if (!url) return;
        await mutate(`${url}/${pmId}`, { method: 'PATCH', body: payload });
        await fetch();
    }

    async function remove(pmId: string): Promise<void> {
        const url = base();
        if (!url) return;
        await mutate(`${url}/${pmId}`, { method: 'DELETE' });
        await fetch();
    }

    async function add(pmId: string): Promise<void> {
        const url = base();
        if (!url) return;
        await mutate(url, { method: 'POST', body: { payment_method: pmId } });
        await fetch();
    }

    /**
     * Hold nothing of the payer: the payer changed (a page let go of its member, or another
     * member's account took its place), and the previous payer's wallet goes with them. A read of
     * their wallet still on its way lands nowhere.
     */
    function reset(): void {
        sequence++;
        methods.value = [];
        loading.value = false;
        error.value = null;
    }

    // The primary method first, so every consumer renders it at the top without its own sort.
    // Stable: the primary flag is the only comparator, so the others keep the API's order.
    const sortedMethods = computed(() =>
        [...methods.value].sort((a, b) => Number(b.is_primary) - Number(a.is_primary)),
    );

    return { methods, sortedMethods, loading, error, fetch, reset, setPrimary, edit, remove, add };
}
